/**
 * Derives how to render a user's notification reachability in admin tables.
 *
 * Source of truth is the OS-level permission (push_permission_status), reported
 * by the mobile client on every app foreground. We deliberately do NOT key off
 * push_notifications_enabled alone: that flag is the in-app preference captured
 * once at token registration and never re-synced, so it shows "on" forever even
 * after a user revokes notifications in system Settings. Showing the OS truth is
 * what stops the admin panel from lying about who can actually be reached.
 *
 * Falls back to the legacy in-app flag (clearly labelled) for users who haven't
 * reported an OS status yet (older app builds / pre-sync accounts).
 */

function asDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function relativeTime(date) {
  if (!date) return '';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * @param {object} item - a user row (global users/{uid} doc fields)
 * @returns {{icon: string, color: string, label: string}}
 */
export function notificationStatusDisplay(item = {}) {
  const status = item.push_permission_status;
  const syncedAt = asDate(item.push_permission_synced_at);
  const syncedSuffix = syncedAt ? ` · synced ${relativeTime(syncedAt)}` : '';

  switch (status) {
    case 'authorized':
      return { icon: 'notifications_active', color: '#2e7d32', label: `Notifications allowed (device)${syncedSuffix}` };
    case 'provisional':
      return { icon: 'notifications_paused', color: '#f9a825', label: `Provisional — delivered quietly, no banners${syncedSuffix}` };
    case 'denied':
      return { icon: 'notifications_off', color: '#c62828', label: `Notifications OFF in device settings — won't see pushes${syncedSuffix}` };
    case 'notDetermined':
      return { icon: 'notifications_none', color: '#b0bec5', label: `Notification permission not decided yet${syncedSuffix}` };
    default:
      // No OS status reported yet — fall back to the in-app preference, labelled
      // so operators know it isn't confirmed device truth.
      return item.push_notifications_enabled === true
        ? { icon: 'notifications_active', color: '#90a4ae', label: 'App preference on (device status not synced yet)' }
        : { icon: 'notifications_off', color: '#b0bec5', label: 'App preference off (device status not synced yet)' };
  }
}

export default notificationStatusDisplay;
